import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '../../context/UserContext';
import useIsMobile from '../../utils/useIsMobile';
import EditActions from '../../beta/dialogs/editActions';
import { Accordion, AccordionSummary, AccordionDetails, Grid, Button as MUIButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Button } from 'primereact/button';

/**
 * Macros component displays and lets users add/edit/delete chat macros.
 *
 * @param {object} props
 * @param {string} props.backgroundColor Base hex color for styling accordions
 * @param {function} props.openDialogComponent fn to open named dialogs
 * @param {object} props.userSettings Contains macros & twitchPermissions
 * @param {function} props.update fn to update userSettings
 * @param {function} props.refresh fn to refresh userSettings
 * @param {function} props.deletekey fn to delete a key path from settings
 */
const Macros = ({backgroundColor = '#5D3B9E', openDialogComponent, userSettings = {}, update = () => {}, refresh = () => {}, deletekey = () => {}}) => {
    // Safe defaults for macros & permissions
    const { macros: initialMacros = {}, twitchPermissions = [] } = userSettings;
    const { editor } = useUser();
    const isMobile = useIsMobile();

    // Local state for optimistic UI and editing
    const [macros, setMacros] = useState(() => ({...initialMacros}));
    const [selectedMacro, setSelectedMacro] = useState(null);
    const [actionList, setActionList] = useState([]);

    // Whenever the parent’s macros change (e.g. after refresh), overwrite local state
    useEffect(() => {
        setMacros({ ...initialMacros });
    }, [initialMacros]);

    const colorScheme = useMemo(() => {
        const hex = backgroundColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return {
            background10: `rgba(${r}, ${g}, ${b}, 0.1)`,
            background50: `rgba(${r}, ${g}, ${b}, 0.5)`
        };
    }, [backgroundColor]);

    const permissionDictionary = useMemo(() => ({
        'create announcement': ['moderator:manage:announcements'],
        'create clip': ['clips:edit'],
        'create shoutout': ['moderator:manage:shoutouts'],
        'snooze next ad': ['channel:manage:ads'],
        'start commercial': ['channel:edit:commercial'],
        'update category': ['channel:manage:broadcast'],
        'update title': ['channel:manage:broadcast']
    }), []);

    const hasPermissionScope = useCallback(scopes => scopes.length === 0 || scopes.some(s => twitchPermissions.includes(s)), [twitchPermissions]);

    const getMissingScopes = useCallback(actions => {
            const missing = new Set();
            actions.forEach(act => {
                const type = (act.type || '').toLowerCase();
                if (type === 'twitch') {
                    const scopes = permissionDictionary[act.action] || [];
                    if (!hasPermissionScope(scopes)) scopes.forEach(s => missing.add(s));
                }
                if (type === 'compare' && Array.isArray(act.actions)) {
                    act.actions.forEach(sub => {
                        if ((sub.type || '').toLowerCase() === 'twitch') {
                            const scopes = permissionDictionary[sub.action] || [];
                            if (!hasPermissionScope(scopes)) scopes.forEach(s => missing.add(s));
                        }
                    });
                }
            });
            return Array.from(missing);
        }, [permissionDictionary, hasPermissionScope]
    );

    const handleAccordionChange = useCallback(name => (_e, isOpen) => {
        if (isOpen) {
            setSelectedMacro(name);
            setActionList(macros[name]?.actions || []);
        }
    }, [macros]);

    const handleSaveActions = useCallback(updatedActions => {
        if (!selectedMacro) return;
        setMacros(prev => {
            const next = {...prev, [selectedMacro]: {...prev[selectedMacro], actions: updatedActions}};
            update({macros: next});
            return next;
        });
    }, [selectedMacro, update]);

    const hideScrollbar = {
        '&::-webkit-scrollbar': {display: 'none'},
        '-ms-overflow-style': 'none',
        'scrollbar-width': 'none'
    };

    return (
        <Grid container sx={{...hideScrollbar, maxHeight: '80vh', overflowY: 'auto', p: isMobile ? 2 : 4}}>
            {/* ADD NEW MACRO */}
            <Button label="Add New" icon="pi pi-plus" size="small" style={{ marginBottom: 16, width: isMobile ? '100%' : undefined }}
                onClick={() => openDialogComponent('newmacro')} />
            {/* LIST EXISTING */}
            {Object.entries(macros).map(([name, macro]) => {
                const missing = getMissingScopes(macro.actions || []);
                const actionCount = (macro.actions || []).length;
                const onDelete = () => {
                    // 1) Optimistically remove locally so UI updates instantly
                    setMacros(prev => {
                        const next = { ...prev };
                        delete next[name];
                        return next;
                    });
                    // 2) Call delete and then refresh parent data
                    deletekey(['macros', name]);
                    refresh();
                };
            return (
                <Grid item xs={12} key={name}>
                    <Accordion sx={{backgroundColor: colorScheme.background10}} onChange={handleAccordionChange(name)}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{backgroundColor: missing.length ? 'rgba(255,255,0,0.1)' : colorScheme.background50, position: 'relative'}}>
                            <h3 style={{ color: 'var(--complementary-color)' }}>{name} ({actionCount} actions)</h3>
                            {/* Desktop Edit/Delete */}
                            {!isMobile && (
                                <div style={{position: 'absolute', right: 64, top: '25%', display: 'flex', gap: 8}}>
                                    <Button label="Edit" icon="pi pi-pencil" size="small" onClick={e => {
                                        e.stopPropagation();
                                        openDialogComponent('newmacro', macro);
                                    }} />
                                    <Button label="Delete" icon="pi pi-trash" size="small" className="p-button-danger" onClick={e => {
                                        e.stopPropagation();
                                        onDelete();
                                    }} />
                                </div>
                            )}
                            {/* Permission Warning */}
                            {missing.length > 0 && !editor && !isMobile && (
                                <MUIButton size="medium" variant="text" color="warning" startIcon={<PriorityHighIcon />} onClick={e => {
                                    e.stopPropagation();
                                    openDialogComponent('addtwitchpermission', { scope: missing });
                                    }} sx={{ position: 'absolute', right: 0, bottom: '30%' }}>
                                    Action Required
                                </MUIButton>
                            )}
                        </AccordionSummary>
                            <AccordionDetails sx={{ p: isMobile ? 2 : 4 }}>
                                {/* Mobile Edit/Delete */}
                                {isMobile && (
                                    <Grid container spacing={2} mb={2}>
                                        <Grid item xs={6}>
                                            <Button label="Edit" icon="pi pi-pencil" size="small" style={{ width: '100%' }} onClick={() => openDialogComponent('newmacro', macro)}/>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Button label="Delete" icon="pi pi-trash" size="small" className="p-button-danger" style={{ width: '100%' }} onClick={onDelete}/>
                                        </Grid>
                                    </Grid>
                                )}
                                {/* Edit Actions Sub-dialog */}
                                <EditActions actionsArr={actionList} setActionsArr={setActionList} backgroundColor={backgroundColor}
                                    userSettings={userSettings} excludeArr={['macro']} save={handleSaveActions} />
                            </AccordionDetails>
                        </Accordion>
                </Grid>
            );
        })}
        </Grid>
    );
};

Macros.propTypes = {
    backgroundColor: PropTypes.string,
    openDialogComponent: PropTypes.func.isRequired,
    userSettings: PropTypes.shape({macros: PropTypes.objectOf(PropTypes.shape({actions: PropTypes.arrayOf(PropTypes.object)})), twitchPermissions: PropTypes.arrayOf(PropTypes.string)}),
    update: PropTypes.func.isRequired,
    refresh: PropTypes.func.isRequired,
    deletekey: PropTypes.func.isRequired
};

export default memo(Macros);